import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MoreHorizontal, RefreshCw, Table2, Trash2, LineChart as LineChartIcon } from 'lucide-react';
import type {
  BarWidgetData,
  GaugeWidgetData,
  HeatmapWidgetData,
  ListWidgetData,
  StatusGridWidgetData,
  StatWidgetData,
  TableWidgetData,
  TimeseriesWidgetData,
  WidgetDefinition,
} from '@itr/shared';
import { getLocale, formatMetric, formatBytes, formatDuration, healthToken, intentToken } from '@itr/shared';
import { api } from '../api/client';
import { useRealtime } from '../api/realtime';
import { BarList, Gauge, Heatmap, Sparkline, TimeseriesChart } from './charts';
import { Badge, EmptyState, ErrorState, Health, Skeleton, SkeletonTable, autoIntent } from './ui';

export interface WidgetInstance {
  instanceId: string;
  widgetId: string;
  title: string | null;
  options: Record<string, unknown>;
}

/**
 * Renders one dashboard widget.
 *
 * The plugin returns *data*, never markup, so the same payload drives the web
 * portal, the desktop shell and the mobile app. Live widgets are pushed over the
 * websocket; the HTTP poll is only a fallback for the first paint and for
 * clients whose socket is down.
 */
export function WidgetRenderer({
  instance,
  definition,
  editing,
  onRemove,
  onConfigure,
}: {
  instance: WidgetInstance;
  definition: (WidgetDefinition & { fullId: string }) | undefined;
  editing?: boolean;
  onRemove?: () => void;
  onConfigure?: () => void;
}) {
  const { watchWidget, on, connected } = useRealtime();
  const [data, setData] = useState<unknown>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [asTable, setAsTable] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const optionsKey = JSON.stringify(instance.options);

  const load = useMemo(
    () => async (showSpinner: boolean) => {
      if (showSpinner) setRefreshing(true);
      try {
        const result = await api.get(`/api/widgets/${encodeURIComponent(instance.widgetId)}/data`, instance.options);
        setData(result);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [instance.widgetId, optionsKey],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // Live push. The server recomputes watched widgets on its own cadence, so this
  // replaces polling entirely while the socket is healthy.
  useEffect(() => {
    if (!connected) return;
    const stop = watchWidget(instance.instanceId, instance.widgetId, instance.options);
    const off = on((event) => {
      if (event.type === 'widget.data' && event.instanceId === instance.instanceId) {
        setData(event.data);
        setLoading(false);
        setError(null);
      }
    });
    return () => {
      stop();
      off();
    };
  }, [connected, instance.instanceId, instance.widgetId, optionsKey, watchWidget, on]);

  // Poll only when the socket is unavailable, so we never do both at once.
  useEffect(() => {
    if (connected) return;
    const refreshSec = definition?.refreshSec ?? 30;
    if (refreshSec <= 0) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load(false);
    }, refreshSec * 1000);
    return () => window.clearInterval(timer);
  }, [connected, definition?.refreshSec, load]);

  const title = instance.title ?? definition?.title ?? instance.widgetId;
  const type = definition?.type ?? 'stat';
  // Charts with sub-3:1 fills owe the reader a relief channel; a table view of
  // the same numbers satisfies that and is genuinely useful besides.
  const tableToggleable = type === 'timeseries' || type === 'bar' || type === 'heatmap';

  return (
    <div className="widget" data-editing={editing || undefined}>
      <header className={`widget-header${editing ? ' widget-drag-handle' : ''}`}>
        <span className="widget-title" title={definition?.description}>
          {title}
        </span>
        <div className="widget-actions">
          {tableToggleable && (
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={() => setAsTable((v) => !v)}
              title={asTable ? 'Show chart' : 'Show as table'}
              aria-label={asTable ? 'Show chart' : 'Show as table'}
            >
              {asTable ? <LineChartIcon size={13} /> : <Table2 size={13} />}
            </button>
          )}
          <button
            className="btn btn-ghost btn-icon btn-sm"
            onClick={() => void load(true)}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={13} className={refreshing ? 'spin' : undefined} />
          </button>
          {editing && onConfigure && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onConfigure} title="Configure" aria-label="Configure">
              <MoreHorizontal size={13} />
            </button>
          )}
          {editing && onRemove && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onRemove} title="Remove" aria-label="Remove">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </header>

      <div className="widget-body">
        {loading ? (
          <WidgetSkeleton type={type} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void load(true)} />
        ) : (
          <WidgetBody type={type} data={data} asTable={asTable} />
        )}
      </div>
    </div>
  );
}

function WidgetSkeleton({ type }: { type: string }) {
  if (type === 'stat') {
    return (
      <div className="stat">
        <Skeleton height={30} width="52%" />
        <Skeleton height={11} width="38%" style={{ marginTop: 6 }} />
      </div>
    );
  }
  if (type === 'table' || type === 'list') return <SkeletonTable rows={4} cols={4} />;
  return <Skeleton height="100%" style={{ minHeight: 80 }} />;
}

function WidgetBody({ type, data, asTable }: { type: string; data: unknown; asTable: boolean }) {
  if (!data) return <EmptyState title="No data" text="This widget has not received any data yet." />;

  switch (type) {
    case 'stat':
      return <StatView data={data as StatWidgetData} />;
    case 'gauge': {
      const g = data as GaugeWidgetData;
      return <Gauge value={g.value} min={g.min} max={g.max} unit={g.unit} label={g.label} thresholds={g.thresholds} />;
    }
    case 'timeseries': {
      const ts = data as TimeseriesWidgetData;
      if (ts.series.length === 0) return <EmptyState title="No samples yet" text="Data will appear once collection runs." />;
      return asTable ? <SeriesTable data={ts} /> : <TimeseriesChart series={ts.series} unit={ts.unit} stacked={ts.stacked} />;
    }
    case 'bar': {
      const b = data as BarWidgetData;
      return asTable ? <BarTable data={b} /> : <BarList bars={b.bars} unit={b.unit} />;
    }
    case 'table':
      return <TableView data={data as TableWidgetData} />;
    case 'status-grid':
      return <StatusGridView data={data as StatusGridWidgetData} />;
    case 'list':
      return <ListView data={data as ListWidgetData} />;
    case 'heatmap': {
      const h = data as HeatmapWidgetData;
      return asTable ? <HeatmapTable data={h} /> : <Heatmap {...h} />;
    }
    default:
      return <EmptyState title="Unsupported widget" text={`This client cannot render a "${type}" widget.`} />;
  }
}

/* ------------------------------------------------------------------ */
/* Views                                                               */
/* ------------------------------------------------------------------ */

function StatView({ data }: { data: StatWidgetData }) {
  const color =
    data.intent === 'good'
      ? 'var(--itr-success)'
      : data.intent === 'warn'
        ? 'var(--itr-warning)'
        : data.intent === 'bad'
          ? 'var(--itr-critical)'
          : 'var(--itr-text)';

  return (
    <div className="stat">
      <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
        <span className="stat-value" style={{ color }}>
          {typeof data.value === 'number' ? formatMetric(data.value, data.unit) : data.value}
        </span>
        {data.deltaPct !== undefined && data.deltaPct !== null && (
          <span
            className="stat-delta"
            style={{ color: data.deltaPct >= 0 ? 'var(--itr-success)' : 'var(--itr-critical)' }}
          >
            {data.deltaPct >= 0 ? '▲' : '▼'} {Math.abs(data.deltaPct).toFixed(1)}%
          </span>
        )}
      </div>
      {data.label && <div className="stat-label">{data.label}</div>}
      {data.spark && data.spark.length > 1 && <Sparkline points={data.spark} height={26} />}
      {data.footnote && <div className="stat-foot">{data.footnote}</div>}
    </div>
  );
}

function TableView({ data }: { data: TableWidgetData }) {
  const navigate = useNavigate();
  if (data.rows.length === 0) return <EmptyState title="Nothing here" text="No rows matched." />;

  const go = (row: Record<string, unknown>) => {
    if (!data.rowLink) return;
    navigate(data.rowLink.replace(/:(\w+)/g, (_, key: string) => String(row[key] ?? '')));
  };

  return (
    <div className="table-wrap" style={{ flex: 1, minHeight: 0 }}>
      <table className="table">
        <thead>
          <tr>
            {data.columns.map((col) => (
              <th key={col.key} data-align={col.align} style={col.width ? { width: col.width } : undefined}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr
              key={String(row.id ?? i)}
              data-clickable={data.rowLink ? 'true' : undefined}
              onClick={() => go(row)}
              tabIndex={data.rowLink ? 0 : undefined}
              onKeyDown={(e) => {
                if (data.rowLink && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  go(row);
                }
              }}
            >
              {data.columns.map((col) => (
                <td key={col.key} data-align={col.align}>
                  <Cell value={row[col.key]} format={col.format} row={row} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ value, format, row }: { value: unknown; format?: string; row?: Record<string, unknown> }) {
  if (value === null || value === undefined || value === '') return <span className="subtle">—</span>;

  switch (format) {
    case 'health': {
      // Widget rows are plugin-shaped, so `statusMessage` is a convention, not
      // a guarantee — take it when the row happens to carry one.
      const reason = typeof row?.statusMessage === 'string' ? row.statusMessage : null;
      return <Health health={String(value)} reason={reason} />;
    }
    case 'badge':
      return <Badge intent={autoIntent(String(value))}>{String(value)}</Badge>;
    case 'bytes':
      return <span className="nums">{formatBytes(Number(value))}</span>;
    case 'duration':
      return <span className="nums">{formatDuration(Number(value))}</span>;
    case 'datetime':
      return (
        <span className="nums" title={new Date(Number(value)).toLocaleString(getLocale())}>
          {new Date(Number(value)).toLocaleString(getLocale(), {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      );
    case 'number':
      return <span className="nums">{new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 1 }).format(Number(value))}</span>;
    case 'currency':
      return <span className="nums">{String(value)}</span>;
    default:
      return <span>{String(value)}</span>;
  }
}

function StatusGridView({ data }: { data: StatusGridWidgetData }) {
  if (data.tiles.length === 0) {
    return <EmptyState title="Nothing monitored yet" text="Add resources to see them here." />;
  }
  return (
    <div className="status-grid">
      {data.tiles.map((tile) => {
        const owner = [tile.client, tile.site].filter(Boolean).join(' · ');
        const content = (
          <>
            <span className="status-tile-label">{tile.label}</span>
            {owner && <span className="status-tile-sub status-tile-owner">{owner}</span>}
            {tile.sublabel && <span className="status-tile-sub">{tile.sublabel}</span>}
          </>
        );
        const style = { ['--tile-color' as string]: healthToken(tile.health) };
        const title = [tile.label, owner, tile.health].filter(Boolean).join(' — ');
        return tile.link ? (
          <Link key={tile.id} to={tile.link} className="status-tile" data-health={tile.health} style={style} title={title}>
            {content}
          </Link>
        ) : (
          <div key={tile.id} className="status-tile" data-health={tile.health} style={style}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

function ListView({ data }: { data: ListWidgetData }) {
  if (data.items.length === 0) {
    return <EmptyState title="All clear" text="Nothing needs your attention right now." />;
  }
  return (
    <div className="col" style={{ gap: 0, overflowY: 'auto', flex: 1, minHeight: 0 }}>
      {data.items.map((item) => {
        const body = (
          <>
            <span
              style={{
                width: 3,
                alignSelf: 'stretch',
                borderRadius: 2,
                background: intentToken(item.intent),
                flexShrink: 0,
              }}
            />
            <span className="grow" style={{ minWidth: 0 }}>
              <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 540 }}>
                {item.primary}
              </span>
              {item.secondary && (
                <span className="truncate subtle" style={{ display: 'block', fontSize: 11.5 }}>
                  {item.secondary}
                </span>
              )}
            </span>
            {item.trailing && (
              <span className="subtle nums" style={{ fontSize: 11.5, flexShrink: 0 }}>
                {item.trailing}
              </span>
            )}
          </>
        );

        const className = 'row';
        const style: React.CSSProperties = {
          gap: 9,
          padding: '8px 2px',
          borderBottom: 'var(--itr-border-w) solid var(--itr-border)',
          color: 'inherit',
        };

        return item.link ? (
          <Link key={item.id} to={item.link} className={className} style={{ ...style, textDecoration: 'none' }}>
            {body}
          </Link>
        ) : (
          <div key={item.id} className={className} style={style}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

/* --- Table fallbacks for charts (the contrast relief channel) --------- */

function SeriesTable({ data }: { data: TimeseriesWidgetData }) {
  const timestamps = data.series[0]?.points.map(([ts]) => ts) ?? [];
  // Newest first and capped: a table view is for reading recent values, not for
  // scrolling three thousand rows.
  const rows = timestamps.slice(-40).reverse();

  return (
    <div className="table-wrap" style={{ flex: 1, minHeight: 0 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Time</th>
            {data.series.map((s) => (
              <th key={s.name} data-align="right">
                {s.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((ts) => {
            const index = timestamps.indexOf(ts);
            return (
              <tr key={ts}>
                <td className="nums">{new Date(ts).toLocaleString(getLocale(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                {data.series.map((s) => (
                  <td key={s.name} data-align="right">
                    {formatMetric(s.points[index]?.[1] ?? null, data.unit)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BarTable({ data }: { data: BarWidgetData }) {
  return (
    <div className="table-wrap" style={{ flex: 1, minHeight: 0 }}>
      <table className="table">
        <thead>
          <tr>
            <th>Item</th>
            <th data-align="right">Value</th>
          </tr>
        </thead>
        <tbody>
          {data.bars.map((bar, i) => (
            <tr key={`${bar.label}-${i}`}>
              <td>{bar.label}</td>
              <td data-align="right">{formatMetric(bar.value, data.unit)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HeatmapTable({ data }: { data: HeatmapWidgetData }) {
  return (
    <div className="table-wrap" style={{ flex: 1, minHeight: 0 }}>
      <table className="table">
        <thead>
          <tr>
            <th />
            {data.xLabels.map((x) => (
              <th key={x} data-align="right">
                {x}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.yLabels.map((y, yi) => (
            <tr key={y}>
              <td style={{ fontWeight: 560 }}>{y}</td>
              {data.xLabels.map((x, xi) => (
                <td key={x} data-align="right">
                  {data.cells[yi]?.[xi] ?? 0}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
